import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { assetsAPI, monitoringAPI, endpointAPI } from '../../services/api';
import AssetPlatformFields, { AssetPlatformValues, assetToPlatformValues, emptyAssetPlatformValues, hasAnyPlatform } from '../../components/assets/AssetPlatformFields';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface AssetDetails {
  id: number;
  name: string;
  description?: string;
  organisation_id?: number;
  tracking_id: string;
  api_key: string;
  api_secret?: string;
  platform_website: boolean;
  platform_mobile_app: boolean;
  platform_desktop_app: boolean;
  platform_iot: boolean;
  platform_endpoints?: boolean;
  endpoint_count?: number;
  website_urls?: string[];
  mobile_app_config?: any;
  desktop_app_config?: any;
  iot_config?: any;
  is_active: boolean;
  is_verified: boolean;
  verified_at?: string;
  last_ping_at?: string;
  data_retention_days?: number;
  integration_script_url?: string;
  integration_code?: string;
  created_at?: string;
  updated_at?: string;
}

const AssetDetailPage: React.FC = () => {
  useDocumentTitle('Asset Details');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assets, setAssets, setCurrentAsset } = useAuth();

  const [asset, setAsset] = useState<AssetDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; description: string } & AssetPlatformValues>({
    name: '',
    description: '',
    ...emptyAssetPlatformValues(),
  });
  // Endpoint devices grouped under this asset (null = not loaded / not on plan)
  const [devices, setDevices] = useState<any[] | null>(null);
  const [devicesLocked, setDevicesLocked] = useState(false);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAssetDetails();
  }, [id]);

  const loadDevices = async (assetId: number) => {
    try {
      const res = await endpointAPI.list({ asset_id: assetId });
      const d = res.data?.data;
      setDevices(Array.isArray(d) ? d : d?.endpoints || []);
      setDevicesLocked(false);
    } catch (err: any) {
      // 403 = endpoint management not on this plan
      setDevicesLocked(err?.response?.status === 403);
      setDevices([]);
    }
  };

  const moveDevice = async (deviceId: number, targetAssetId: number) => {
    setMovingId(deviceId);
    try {
      await endpointAPI.update(deviceId, { asset_id: targetAssetId });
      const target = assets.find((a) => a.id === targetAssetId);
      toast.success(`Device moved to ${target?.name || 'the selected asset'}`);
      if (asset) loadDevices(asset.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to move device');
    } finally {
      setMovingId(null);
    }
  };

  const addDevice = () => {
    if (!asset) return;
    const ctxAsset = assets.find((a) => a.id === asset.id);
    if (ctxAsset) setCurrentAsset(ctxAsset);
    navigate('/endpoints/add');
  };

  const fetchAssetDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await assetsAPI.get(parseInt(id));

      if (response.data.status === 'success') {
        const assetData = response.data.data;
        setAsset(assetData);
        setEditForm({
          name: assetData.name || '',
          description: assetData.description || '',
          ...assetToPlatformValues(assetData),
        });
        loadDevices(assetData.id);
      } else {
        toast.error('Failed to load asset details');
        navigate('/assets');
      }
    } catch (error: any) {
      console.error('Error fetching asset:', error);
      toast.error(error.response?.data?.message || 'Failed to load asset');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!asset) return;

    try {
      setActionLoading(true);
      const response = await assetsAPI.update(asset.id, {
        is_active: !asset.is_active
      });

      if (response.data.status === 'success') {
        const newStatus = !asset.is_active;
        setAsset({ ...asset, is_active: newStatus });

        // Update assets list in context
        const updatedAssets = assets.map(a =>
          a.id === asset.id ? { ...a, is_active: newStatus } : a
        );
        setAssets(updatedAssets);

        toast.success(newStatus ? 'Asset activated' : 'Asset deactivated');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update asset');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!asset) return;

    try {
      setActionLoading(true);
      const response = await assetsAPI.delete(asset.id);

      if (response.data.status === 'success') {
        // Remove from assets list in context
        const updatedAssets = assets.filter(a => a.id !== asset.id);
        setAssets(updatedAssets);

        // If this was the current asset, switch to another
        if (updatedAssets.length > 0) {
          setCurrentAsset(updatedAssets[0]);
        }

        toast.success('Asset deleted successfully');
        navigate('/assets');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete asset');
    } finally {
      setActionLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    if (!editForm.name.trim()) { toast.error('Please enter an asset name'); return; }
    if (!hasAnyPlatform(editForm)) { toast.error('Please select at least one asset type'); return; }
    const urls = editForm.website_urls.map((u) => u.trim()).filter((u) => u && u !== 'https://');
    if (editForm.platform_website && urls.length === 0) { toast.error('Please add at least one website URL'); return; }

    try {
      setActionLoading(true);
      const response = await assetsAPI.update(asset.id, {
        name: editForm.name.trim(),
        description: editForm.description,
        platform_website: editForm.platform_website,
        platform_mobile_app: editForm.platform_mobile_app,
        platform_desktop_app: editForm.platform_desktop_app,
        platform_iot: editForm.platform_iot,
        platform_endpoints: editForm.platform_endpoints,
        website_urls: urls,
        mobile_app_config: editForm.mobile_app_config,
        desktop_app_config: editForm.desktop_app_config,
        iot_config: editForm.iot_config,
      });

      if (response.data.status === 'success') {
        const updatedAssets = assets.map(a =>
          a.id === asset.id
            ? { ...a, name: editForm.name.trim(), description: editForm.description,
                platform_website: editForm.platform_website, platform_mobile_app: editForm.platform_mobile_app,
                platform_desktop_app: editForm.platform_desktop_app, platform_iot: editForm.platform_iot,
                platform_endpoints: editForm.platform_endpoints } as any
            : a
        );
        setAssets(updatedAssets);
        toast.success('Asset updated successfully');
        setShowEditModal(false);
        fetchAssetDetails();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update asset');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!asset) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <h4>Asset not found</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/assets')}>
            Back to Assets
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <button
                  className="btn btn-link text-muted p-0 mb-2"
                  onClick={() => navigate('/assets')}
                >
                  <i className="bi bi-arrow-left me-1"></i> Back to Assets
                </button>
                <h2 className="h3 mb-1">{asset.name}</h2>
                <p className="text-muted mb-0">
                  <code className="bg-light px-2 py-1 rounded">{asset.tracking_id}</code>
                </p>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setShowEditModal(true)}
                >
                  <i className="bi bi-pencil me-1"></i> Edit
                </button>
                <button
                  className={`btn ${asset.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                  onClick={handleToggleActive}
                  disabled={actionLoading}
                >
                  <i className={`bi ${asset.is_active ? 'bi-pause-circle' : 'bi-play-circle'} me-1`}></i>
                  {asset.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="btn btn-outline-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <i className="bi bi-trash me-1"></i> Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Status Card */}
          <div className="col-12 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="card-title mb-3">Status</h5>

                <div className="mb-3">
                  <span className="text-muted small d-block mb-1">Integration Status</span>
                  {asset.is_verified ? (
                    <span className="badge bg-success fs-6">
                      <i className="bi bi-check-circle-fill me-1"></i> Live
                    </span>
                  ) : (
                    <span className="badge bg-warning text-dark fs-6">
                      <i className="bi bi-exclamation-triangle-fill me-1"></i> Not Integrated
                    </span>
                  )}
                </div>

                <div className="mb-3">
                  <span className="text-muted small d-block mb-1">Active Status</span>
                  {asset.is_active ? (
                    <span className="badge bg-primary fs-6">Active</span>
                  ) : (
                    <span className="badge bg-secondary fs-6">Inactive</span>
                  )}
                </div>

                {asset.verified_at && (
                  <div className="mb-3">
                    <span className="text-muted small d-block mb-1">Verified At</span>
                    <span>{new Date(asset.verified_at).toLocaleString()}</span>
                  </div>
                )}

                {asset.last_ping_at && (
                  <div className="mb-3">
                    <span className="text-muted small d-block mb-1">Last Activity</span>
                    <span>{new Date(asset.last_ping_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="card-title mb-3">Asset Details</h5>

                {asset.description && (
                  <div className="mb-3">
                    <span className="text-muted small d-block mb-1">Description</span>
                    <p className="mb-0">{asset.description}</p>
                  </div>
                )}

                <div className="mb-3">
                  <span className="text-muted small d-block mb-1">Platforms</span>
                  <div className="d-flex flex-wrap gap-2">
                    {asset.platform_website && <span className="badge bg-primary">Website</span>}
                    {asset.platform_mobile_app && <span className="badge bg-info">Mobile App</span>}
                    {asset.platform_desktop_app && <span className="badge bg-dark">Desktop App</span>}
                    {asset.platform_iot && <span className="badge bg-secondary">IoT</span>}
                    {asset.platform_endpoints && <span className="badge bg-success">Endpoint Devices</span>}
                  </div>
                </div>

                {asset.website_urls && asset.website_urls.length > 0 && (
                  <div className="mb-3">
                    <span className="text-muted small d-block mb-1">Website URLs</span>
                    <ul className="list-unstyled mb-0">
                      {asset.website_urls.map((url, index) => (
                        <li key={index}>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            {url} <i className="bi bi-box-arrow-up-right small"></i>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <span className="text-muted small d-block mb-1">Created</span>
                  <span>{asset.created_at ? new Date(asset.created_at).toLocaleString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Endpoint devices grouped under this asset */}
          {(asset.platform_endpoints || (devices && devices.length > 0)) && (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-laptop me-2 text-success"></i>Endpoint Devices
                      {devices && devices.length > 0 && <span className="badge bg-light text-dark ms-2">{devices.length}</span>}
                    </h5>
                    {!devicesLocked && (
                      <button className="btn btn-sm btn-primary" onClick={addDevice}>
                        <i className="bi bi-plus-circle me-1"></i>Add laptop / desktop
                      </button>
                    )}
                  </div>
                  {devicesLocked ? (
                    <div className="alert alert-light border mb-0">
                      <i className="bi bi-lock me-2"></i>Endpoint management isn't included in your current plan.{' '}
                      <a href="/billing/plans">View plans</a>
                    </div>
                  ) : devices === null ? (
                    <div className="text-muted small">Loading devices…</div>
                  ) : devices.length === 0 ? (
                    <div className="text-muted small">
                      No devices in this asset yet. Add a laptop or desktop to start monitoring its health, threats and scans.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr className="text-muted small">
                            <th>Device</th><th>OS</th><th>Status</th><th>Last seen</th><th style={{ width: 230 }}>Asset group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {devices.map((d: any) => (
                            <tr key={d.id}>
                              <td>
                                <a href={`/endpoints/${d.id}`} className="text-decoration-none fw-semibold">{d.name || d.hostname || d.endpoint_id}</a>
                                {d.hostname && d.hostname !== d.name && <div className="text-muted small">{d.hostname}</div>}
                              </td>
                              <td className="small text-capitalize">{[d.os, d.os_version].filter(Boolean).join(' ') || '—'}</td>
                              <td>
                                <span className={`badge ${d.status === 'online' ? 'bg-success' : d.status === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                  {d.status || 'unknown'}
                                </span>
                              </td>
                              <td className="small text-muted">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'Never'}</td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={asset.id}
                                  disabled={movingId === d.id}
                                  onChange={(e) => moveDevice(d.id, parseInt(e.target.value))}
                                  aria-label={`Move ${d.name} to another asset`}
                                >
                                  {assets
                                    .filter((a) => a.organisation_id === asset.organisation_id || a.id === asset.id)
                                    .map((a) => <option key={a.id} value={a.id}>{a.id === asset.id ? `${a.name} (current)` : `Move to ${a.name}`}</option>)}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Integration Code Card */}
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-3">Integration Code</h5>

                {!asset.is_verified && (
                  <div className="alert alert-info mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Add the following code to your website's <code>&lt;head&gt;</code> section to start tracking.
                    The asset will be automatically verified once the script loads.
                  </div>
                )}

                <div className="bg-dark text-light p-3 rounded position-relative">
                  <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <code>{asset.integration_code || `<script async src="${asset.integration_script_url}"></script>`}</code>
                  </pre>
                  <button
                    className="btn btn-sm btn-outline-light position-absolute top-0 end-0 m-2"
                    onClick={() => copyToClipboard(asset.integration_code || `<script async src="${asset.integration_script_url}"></script>`)}
                  >
                    <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* WordPress Plugin Card */}
          {asset.platform_website && (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <i className="bi bi-wordpress me-2" style={{ color: '#21759b' }}></i>
                    WordPress Integration
                  </h5>
                  <div className="d-flex align-items-start gap-4 p-3 rounded" style={{ background: '#f8f9ff', border: '1px solid #e0e3ff' }}>
                    <div style={{ fontSize: 40, color: '#21759b', lineHeight: 1, flexShrink: 0 }}>
                      <i className="bi bi-wordpress"></i>
                    </div>
                    <div className="flex-grow-1">
                      <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                        Use our WordPress plugin to automatically inject the tracking script and optionally auto-capture WooCommerce events (login, registration, purchase) — no manual code editing needed.
                      </p>
                      <div className="d-flex gap-2 flex-wrap">
                        <a
                          href={`${(process.env.REACT_APP_API_URL || 'https://api.cloudayn.com/api').replace('/api', '')}/wordpress-plugin/cloudayn.zip`}
                          download="cloudayn.zip"
                          className="btn btn-primary btn-sm"
                        >
                          <i className="bi bi-download me-2"></i>Download Plugin (v1.3.0)
                        </a>
                        <a href="/integration" className="btn btn-outline-secondary btn-sm">
                          <i className="bi bi-code-slash me-1"></i>Other Integration Methods
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-muted small fw-semibold mb-2">Installation Steps:</p>
                    <ol className="text-muted small mb-0" style={{ paddingLeft: 18, lineHeight: 1.9 }}>
                      <li>Download the plugin zip above.</li>
                      <li>In WordPress admin go to <strong>Plugins → Add New → Upload Plugin</strong>.</li>
                      <li>Upload <code>cloudayn.zip</code> and click <strong>Install Now</strong>, then activate.</li>
                      <li>Go to <strong>CloudAyn → Integration</strong> and sign in with your CloudAyn email via OTP.</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Keys Card */}
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-3">API Credentials</h5>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small">API Key</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={asset.api_key}
                        readOnly
                      />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => copyToClipboard(asset.api_key)}
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </div>
                  {asset.api_secret && (
                    <div className="col-md-6">
                      <label className="form-label text-muted small">API Secret</label>
                      <div className="input-group">
                        <input
                          type="password"
                          className="form-control bg-light"
                          value={asset.api_secret}
                          readOnly
                        />
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => copyToClipboard(asset.api_secret!)}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Asset</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete <strong>{asset.name}</strong>?</p>
                  <p className="text-danger mb-0">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    This action cannot be undone. All analytics data associated with this asset will be permanently deleted.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete Asset'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content">
                <form onSubmit={handleEdit}>
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Asset</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowEditModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label className="form-label">Asset Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      />
                    </div>
                    {/* Same asset types and settings as "Add asset" */}
                    <AssetPlatformFields
                      value={editForm}
                      onChange={(next) => setEditForm({ ...editForm, ...next })}
                      disabled={actionLoading}
                      endpointsFooter={
                        <div className="small">
                          {devices && devices.length > 0
                            ? <span className="text-success"><i className="bi bi-check-circle me-1"></i>{devices.length} device{devices.length > 1 ? 's' : ''} in this asset.</span>
                            : <span className="text-muted">After saving, use <strong>Add laptop / desktop</strong> on this page to enrol devices.</span>}
                        </div>
                      }
                    />
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AssetDetailPage;
